// src/pages/admin/TokenManagement.tsx
import React, { useEffect, useState } from 'react';
import { Shield, RefreshCcw, KeyRound } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { adminGetTokenStatus, adminRefresh, TokenStatus, openCchAuthorizePopup } from '../../services/admin';

export default function TokenManagement() {
  const { ready, isAdmin } = useAuth();
  const [status, setStatus] = useState<TokenStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadStatus() {
    setLoading(true);
    setError(null);
    try {
      const s = await adminGetTokenStatus();
      setStatus(s);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
  function onMsg(ev: MessageEvent) {
    if (ev?.data?.type === 'cch_oauth_done') {
      console.log('[CCH OAuth] popup reported done', ev.data);
      loadStatus().catch((e) => console.warn('[CCH OAuth] loadStatus failed', e));
    }
  }
  window.addEventListener('message', onMsg);
  return () => window.removeEventListener('message', onMsg);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  useEffect(() => {
    if (!ready || !isAdmin) return;
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, isAdmin]);

  if (!ready) return null;
  if (!isAdmin) {
    return (
      <section className="card">
        <div className="hd">
          <div className="row">
            <div style={{ fontWeight: 760 }}>Admin • Token Management</div>
          </div>
        </div>
        <div className="bd">
          <div className="muted">You do not have access to this page.</div>
        </div>
      </section>
    );
  }

  return (
    <div className="page-stack">
      <section className="card">
        <div className="hd">
          <div className="row">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontWeight: 760 }}>Admin • Token Management</div>
              <div className="muted" style={{ fontSize: 13 }}>
                Authorize + refresh CCH OAuth tokens. Refresh token is never returned to the client.
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                className="btn"
                type="button"
                disabled={loading}
                onClick={async () => {
                  setError(null);

                  // IMPORTANT: open popup, do not fetch()
                  const popup = openCchAuthorizePopup();

                  // Optional: poll status until popup closes (or timeout)
                  const started = Date.now();
                  const poll = setInterval(async () => {
                    const closed = !popup || popup.closed;
                    const tooLong = Date.now() - started > 2 * 60 * 1000;
                    if (closed || tooLong) {
                      clearInterval(poll);
                      await loadStatus().catch(() => {});
                    }
                  }, 1200);
                }}
              >
                <KeyRound size={16} /> Authorize
              </button>

              <button
                className="btn"
                type="button"
                disabled={loading}
                onClick={async () => {
                  setLoading(true);
                  setError(null);
                  try {
                    const s = await adminRefresh();
                    setStatus(s);
                  } catch (e: any) {
                    setError(e?.message || String(e));
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                <RefreshCcw size={16} /> Force Refresh
              </button>

              <button className="btn" type="button" disabled={loading} onClick={loadStatus}>
                <Shield size={16} /> Reload Status
              </button>
            </div>
          </div>
        </div>

        <div className="bd">
          {error ? <div className="muted">Error: {error}</div> : null}

          <div className="kv" style={{ marginTop: 10 }}>
            <div className="field">
              <div className="k">Has Access Token</div>
              <div className="v">{status?.hasAccessToken ? 'Yes' : 'No'}</div>
            </div>
            <div className="field">
              <div className="k">Access Token Expires</div>
              <div className="v">{status?.accessTokenExpiresAt || '—'}</div>
            </div>
            <div className="field">
              <div className="k">Last Updated</div>
              <div className="v">{status?.lastUpdatedAt || '—'}</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}